// V2-Events embed config + token acquisition. Second instance of the V2-GAC embed pattern (see
// utils/v2gac.ts, which documents the security model in detail).
//
// PRODUCTION PATH (default): the token is minted SERVER-SIDE by HotService (`POST events/v2/token`,
// EventsV2Controller) — the server derives playerId AND the GOH guildId from the authenticated session
// and stamps aud="events" (REQUIRED by the Events BFF). The signing secret never reaches the browser.
//
// DEV FALLBACK (opt-in only): signDevEventsToken() mints the HS256 token IN THE BROWSER with a shared
// secret — forgeable, never in production. Enable explicitly with REACT_APP_V2EVENTS_DEV_SIGN=true.

import BaseAPI from "../service/BaseAPI";

// The embedded HotUtilsV2-Events app's origin, per environment. Override with REACT_APP_V2EVENTS_URL.
export function getV2EventsUrl(): string {
  const override = process.env.REACT_APP_V2EVENTS_URL;
  if (override) return override;

  const host = window.location.host;
  // ⚠️ PROD + STAGING point at the container app's own FQDN, not <app>.hotutils.com. Those subdomains
  // were an aspirational convention copied from the first GAC embed and were NEVER provisioned - all twelve
  // failed to resolve (verified 2026-08-12). There is no staging container app either, so staging uses the
  // PROD one, which already accepts staging.hotutils.com as a framer via its PARENT_ORIGIN allowlist.
  // 🔴 The tab is still gated OFF outside development by isV2EventsEnabled() - fixing the URL first means the
  // wiring is correct with nothing user-visible to roll back.
  if (host === 'hotutils.com' || host === 'www.hotutils.com') return 'https://hotutils-events-prod.happyplant-d18608c3.southcentralus.azurecontainerapps.io';
  if (host.includes('localhost')) return 'http://localhost:5174'; // local Events dev server (GAC uses 5173)
  // Deployed development site -> the deployed dev Events container app.
  if (host.startsWith('development')) return 'https://hotutils-events-dev.graysky-4fd00577.southcentralus.azurecontainerapps.io';
  return 'https://hotutils-events-prod.happyplant-d18608c3.southcentralus.azurecontainerapps.io';
}

export function getV2EventsOrigin(): string {
  return new URL(getV2EventsUrl()).origin;
}

/**
 * The V2-Events tab is DEV-ONLY for now — only surfaced (tab + route) on the Development site and in local
 * dev; suppressed on staging/production until ready. Same gating rule as V2-GAC.
 */
export function isV2EventsEnabled(): boolean {
  const host = window.location.host;
  return host === 'development.hotutils.com' || host.startsWith('localhost');
}

/** True when the dev-only in-browser signer should be used instead of the server endpoint (local dev only). */
export function useEventsDevSigner(): boolean {
  return process.env.REACT_APP_V2EVENTS_DEV_SIGN === 'true';
}

interface EventsV2TokenResponse {
  token?: string;
  issuedAtUnix?: number;
  expiresAtUnix?: number;
}

/**
 * PRODUCTION PATH: ask HotService to mint the embed token (EventsV2Controller, `POST events/v2/token`).
 * The server derives playerId + the GOH guildId from the session and signs with aud="events"; the browser
 * only receives the finished JWT.
 */
export async function fetchEventsToken(sessionId?: string): Promise<string> {
  const resp: EventsV2TokenResponse = await BaseAPI.postToApi('events/v2/token', { sessionId: sessionId ?? null });
  if (!resp || !resp.token) throw new Error('Events token endpoint returned no token');
  return resp.token;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlStr(s: string): string {
  return base64Url(new TextEncoder().encode(s));
}

export interface V2EventsTokenClaims {
  sub: string;
  sessionId?: string;
  allyCode?: string;
  playerId?: string;
  guildId?: string;
}

// The shared HS256 secret the Events BFF verifies with (PARENT_TOKEN_SECRET). Dev default matches the
// Events app's .env.example. Override with REACT_APP_V2EVENTS_DEV_SECRET. Dev fallback signer only.
function getDevSecret(): string {
  return process.env.REACT_APP_V2EVENTS_DEV_SECRET || 'dev-shared-secret-change-me';
}

// The GOH guild id (string) for the DEV SIGNER ONLY. The website client only has the legacy numeric guild
// id, so a dev-signed token's guild scope must come from env config — there is deliberately NO default:
// the production path (HotService) derives the real guild server-side, so this matters only when
// REACT_APP_V2EVENTS_DEV_SIGN=true, and then you must point it at your own test guild.
function getDevGuildId(): string | undefined {
  return process.env.REACT_APP_V2EVENTS_DEV_GUILD_ID || undefined;
}

/**
 * DEV-ONLY FALLBACK: mint an HS256 JWT the Events BFF will verify. Note aud MUST be "events" — the Events
 * BFF requires that audience (unlike the GAC BFF, where aud is optional), so a GAC token can never
 * authorize the Events app and vice versa.
 */
export async function signDevEventsToken(claims: V2EventsTokenClaims): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const devGuildId = getDevGuildId();
  const payload = {
    ...(devGuildId ? { guildId: devGuildId } : {}),
    ...claims,
    iss: 'hotutils',
    aud: 'events',
    iat: now,
    exp: now + 60 * 60, // 1h
  };
  const header = { alg: 'HS256', typ: 'JWT' };
  const data = `${base64UrlStr(JSON.stringify(header))}.${base64UrlStr(JSON.stringify(payload))}`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(getDevSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
  return `${data}.${base64Url(new Uint8Array(sig))}`;
}
